import { useState } from 'react'
import type { ApiToken, ApiTokenCreated, ApiTokenScope } from '@workflow/contracts'
import { API_BASE } from '@workflow/contracts'
import { Button, Checkbox, ConfirmButton, Dropdown, EmptyNote, Field, Icons, Loading, StateNote, TextField, notify } from '@workflow/ui'
import { describeError } from '@/lib/api/client'
import { useApiTokens, useCreateApiToken, useRevokeApiToken, useSession } from '@/lib/api/hooks'
import { formatRelative, formatTimestamp } from '@/lib/format'

/**
 * The machine passport, administered: the base URL anything points at, the
 * keys that open it, and the life of each key on the record. A key acts as
 * the administrator who cut it, capped by its scopes; the secret is shown
 * exactly once — after that, only the prefix exists anywhere.
 */

const SCOPES: readonly { scope: ApiTokenScope; name: string; detail: string }[] = [
  { scope: 'read', name: 'Read', detail: 'See the work — every list and record.' },
  { scope: 'write', name: 'Write', detail: 'Shape the work — create, edit, assign, complete.' },
  { scope: 'admin', name: 'Admin', detail: 'Run the studio — members, audit, the tokens themselves never.' },
]

export function ApiPane() {
  const session = useSession()
  const isAdmin = session.data?.account?.role === 'admin'
  const tokens = useApiTokens()
  const [cutting, setCutting] = useState(false)
  const [created, setCreated] = useState<ApiTokenCreated | null>(null)

  if (!isAdmin) {
    return (
      <StateNote
        title="This part of settings is for administrators."
        detail="API tokens are created by administrators. A token acts as the administrator who created it."
      />
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {created ? (
        <ShowOnce token={created} onDone={() => setCreated(null)} />
      ) : cutting ? (
        <CutForm onDone={(token) => { setCutting(false); setCreated(token) }} onCancel={() => setCutting(false)} />
      ) : (
        <div>
          <Button size="sm" variant="primary" icon={<Icons.plus />} onClick={() => setCutting(true)}>
            Create token
          </Button>
          <p className="mt-1 max-w-[60ch] text-caption text-muted-foreground">
            A token acts as you, limited by its scopes. The secret is shown once, right after creation.
          </p>
        </div>
      )}

      <div>
        <h3 className="text-micro text-muted-foreground uppercase">Base URL</h3>
        <div className="mt-1.5 flex items-center gap-2">
          <code className="rounded-md border bg-surface-sunken px-2 py-1 font-mono text-[13px] text-foreground">
            {window.location.origin}{API_BASE}
          </code>
          <Button
            size="xs"
            variant="ghost"
            icon={<Icons.copy />}
            onClick={() => {
              void navigator.clipboard.writeText(`${window.location.origin}${API_BASE}`)
              notify({ tone: 'success', title: 'The base URL is copied.' })
            }}
          >
            Copy
          </Button>
        </div>
        <p className="mt-1.5 max-w-[60ch] text-caption text-muted-foreground">
          Every request carries the key as <code className="font-mono">Authorization: Bearer wf_…</code>. The
          routes are the same ones this console uses — there is no second API to learn: <code className="font-mono">GET</code>{' '}
          the base URL itself with a key for the full manifest, and <code className="font-mono">GET /whoami</code> for what
          that key may reach.
        </p>
      </div>

      <div>
        <h3 className="text-micro text-muted-foreground uppercase">The keys</h3>
        <div className="mt-1.5">
          {tokens.isLoading ? (
            <Loading label="Loading tokens" />
          ) : tokens.error ? (
            <StateNote title="The API tokens could not be loaded." detail={describeError(tokens.error)} />
          ) : (tokens.data?.length ?? 0) === 0 ? (
            <EmptyNote title="No API tokens yet." detail="Create one when an integration needs API access." />
          ) : (
            <ul className="flex flex-col">
              {tokens.data!.map((token) => (
                <TokenRow key={token.id} token={token} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

/** The creation moment: the secret, in full, for the last time. */
function ShowOnce({ token, onDone }: { token: ApiTokenCreated; onDone: () => void }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="flex flex-col gap-5 rounded-2xl border bg-surface px-5 py-5 shadow-chip">
      <p className="text-ui font-medium text-foreground">Token created: {token.name}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded-md border bg-surface-sunken px-2 py-1.5 font-mono text-[13px] text-foreground">
          {token.token}
        </code>
        <Button
          size="xs"
          variant="ghost"
          icon={<Icons.copy />}
          onClick={() => {
            void navigator.clipboard.writeText(token.token)
            setCopied(true)
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <p className="mt-2 text-caption text-danger">
        Copy it now. This is the only time the secret is shown — the record keeps only its prefix.
      </p>
      {token.expiresAt ? (
        <p className="mt-1 text-caption text-muted-foreground">
          The token stops working on its own on {formatTimestamp(token.expiresAt)} — create a new one then, or revoke it
          earlier.
        </p>
      ) : null}
      <div className="mt-3">
        <Button size="sm" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  )
}

const LIFESPANS: readonly { label: string; days: number | null }[] = [
  { label: 'Until revoked', days: null },
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
  { label: '1 year', days: 365 },
]

function CutForm({ onDone, onCancel }: { onDone: (token: ApiTokenCreated) => void; onCancel: () => void }) {
  const [name, setName] = useState('')
  const [scopes, setScopes] = useState<ApiTokenScope[]>(['read'])
  const [days, setDays] = useState<number | null>(null)
  const create = useCreateApiToken()

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    create.mutate(
      { name: name.trim(), scopes, ...(days === null ? {} : { expiresInDays: days }) },
      { onSuccess: (data) => onDone(data as ApiTokenCreated) },
    )
  }

  const toggle = (scope: ApiTokenScope) =>
    setScopes((current) => (current.includes(scope) ? current.filter((entry) => entry !== scope) : [...current, scope]))

  return (
    <form onSubmit={submit} className="flex flex-col gap-5 rounded-2xl border bg-surface px-5 py-5 shadow-chip">
      <Field label="Name" htmlFor="token-name" hint="What the token is for — it will be named in the audit trail.">
        <TextField
          id="token-name"
          value={name}
          autoFocus
          placeholder="e.g. MCP — Claude"
          onChange={(event) => setName(event.target.value)}
        />
      </Field>
      <Field label="Lifespan" htmlFor="token-lifespan" hint="After this day the token stops working automatically.">
        <Dropdown
          id="token-lifespan"
          ariaLabel="Lifespan"
          value={days === null ? 'never' : String(days)}
          options={LIFESPANS.map((entry) => ({ value: entry.days === null ? 'never' : String(entry.days), label: entry.label }))}
          onChange={(value) => setDays(value === 'never' ? null : Number(value))}
        />
      </Field>
      <fieldset className="min-w-0">
        <legend className="text-micro text-muted-foreground uppercase">Scopes</legend>
        <div className="mt-2 flex flex-col gap-1.5">
          {SCOPES.map((entry) => (
            <Checkbox
              key={entry.scope}
              className="rounded-lg px-2 py-2 hover:bg-tint"
              checked={scopes.includes(entry.scope)}
              onChange={() => toggle(entry.scope)}
            >
              <span className="block text-ui font-medium text-foreground">{entry.name}</span>
              <span className="block text-caption text-muted-foreground">{entry.detail}</span>
            </Checkbox>
          ))}
        </div>
      </fieldset>
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" size="sm" variant="primary" busy={create.isPending} disabled={!name.trim() || scopes.length === 0}>
          Create token
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        {create.error ? <p className="text-caption text-danger">{describeError(create.error)}</p> : null}
      </div>
    </form>
  )
}

function TokenRow({ token }: { token: ApiToken }) {
  const revoke = useRevokeApiToken()
  const dead = token.revokedAt !== null
  const lapsed = token.expiresAt !== null && Date.parse(token.expiresAt) <= Date.now()

  return (
    <li className="border-b border-hairline last:border-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 py-2.5">
        <span className="min-w-0 flex-1">
          <span className="block truncate text-ui font-medium text-foreground">
            {token.name}
            {dead ? <span className="ml-2 text-caption text-muted-foreground">revoked</span> : null}
            {!dead && lapsed ? <span className="ml-2 text-caption text-danger">expired</span> : null}
          </span>
          <span className="block truncate font-mono text-caption text-muted-foreground">
            {token.prefix}… · {token.scopes.join(', ')}
          </span>
        </span>
        <span className="text-caption text-muted-foreground" title="Created">
          created {formatTimestamp(token.createdAt)}
        </span>
        <span className="text-caption text-muted-foreground" title="Last used">
          {token.lastUsedAt === null ? 'never used' : `used ${formatTimestamp(token.lastUsedAt)}`}
        </span>
        {!dead && token.expiresAt !== null ? (
          <span className="text-caption text-muted-foreground" title="Lifespan">
            {lapsed ? `lapsed ${formatRelative(token.expiresAt)}` : `expires ${formatTimestamp(token.expiresAt)}`}
          </span>
        ) : null}
        {!dead ? (
          <ConfirmButton
            label="Revoke"
            ask={{
              title: `Revoke ${token.name}?`,
              detail: 'Anything using this token stops working at once. This cannot be undone.',
              confirmLabel: 'Revoke',
            }}
            busy={revoke.isPending}
            onConfirm={() => revoke.mutate({ id: token.id })}
          />
        ) : null}
      </div>
      {revoke.error ? <p className="px-1 pb-2 text-caption text-danger">{describeError(revoke.error)}</p> : null}
    </li>
  )
}
